export const getFormattedEstablishedDate = (
  month: number | null,
  year: number | null
) => {
  if (!month || !year) return "";

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const now = new Date();
  const establishedDate = new Date(year, month - 1); // month is 1-based

  let diffYears = now.getFullYear() - establishedDate.getFullYear();
  let diffMonths = now.getMonth() - establishedDate.getMonth();

  if (diffMonths < 0) {
    diffMonths += 12;
    diffYears -= 1;
  }

  const formattedData = `${monthNames[month - 1]} ${year}`;
  const formattedWithDiff = `${diffYears} year${
    diffYears !== 1 ? "s" : ""
  }, ${diffMonths} month${diffMonths !== 1 ? "s" : ""}`;

  return [formattedData, formattedWithDiff];
};

export const taxNumberFormatInput = (value: string, previousValue?: string) => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (cvLength <= 2) {
    return currentValue;
  } else if (cvLength <= 7) {
    return `${currentValue.slice(0, 2)}-${currentValue.slice(2)}`;
  } else {
    return `${currentValue.slice(0, 2)}-${currentValue.slice(2, 11)}`;
  }
};

export const socialSecurityNumberFormatInput = (
  value: string,
  previousValue?: string
) => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (cvLength <= 3) {
    return currentValue;
  } else if (cvLength <= 5) {
    return `${currentValue.slice(0, 3)}-${currentValue.slice(3)}`;
  } else {
    return `${currentValue.slice(0, 3)}-${currentValue.slice(
      3,
      5
    )}-${currentValue.slice(5, 11)}`;
  }
};

export const currencyFormatter = (value: string | number): string => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value).replace(/[^0-9.]/g, "");
  let [integerPart, decimalPart] = stringValue.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (decimalPart !== undefined) {
    decimalPart = decimalPart.slice(0, 4);
    return `${formattedInteger}.${decimalPart}`;
  }
  return formattedInteger;
};

export function splitCamelCaseToWords(value: string): string {
  if (!value) return "";
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();
}

/** Form dropdown uses "" for the "Select" option; only Mobile/Office are persisted. */
export function normalizePropertyContactPhoneType(
  phoneType: unknown
): string {
  if (phoneType === "Mobile" || phoneType === "Office") {
    return phoneType;
  }
  return "";
}

/** Coerce API/order payload contacts into form-safe values (e.g. null email → ""). */
export function mapPropertyContactsForFormValues(
  contacts: any[] | undefined | null
): any[] {
  return (contacts || []).map((c: any) => ({
    ...c,
    name: c?.name ?? "",
    email: c?.email ?? "",
    phone: c?.phone ?? "",
    phoneType: normalizePropertyContactPhoneType(c?.phoneType),
  }));
}

export function propertyContactPhoneTypeForApi(
  phoneType: unknown
): string | null {
  if (phoneType === "Mobile" || phoneType === "Office") {
    return phoneType;
  }
  return null;
}

export function propertyContactEmailForApi(email: unknown): string | null {
  if (email == null) return null;
  const trimmed = String(email).trim();
  return trimmed === "" ? null : trimmed;
}

export function pruneEmptyFields<T>(input: T): T {
  if (Array.isArray(input)) {
    const prunedArray = input
      .map((item) => pruneEmptyFields(item))
      .filter((item) => !isEmptyValue(item));
    return prunedArray as unknown as T;
  }

  if (input !== null && typeof input === "object") {
    const result: any = {};
    Object.entries(input as any).forEach(([key, value]) => {
      const pruned = pruneEmptyFields(value as any);
      if (!isEmptyValue(pruned)) {
        result[key] = pruned;
      }
    });
    return result as T;
  }

  return input;
}

function isEmptyValue(value: any): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
